export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isInsideSpan(text, index) {
  const before = text.slice(0, index);
  const openSpans = (before.match(/<span\b/gi) || []).length;
  const closeSpans = (before.match(/<\/span>/gi) || []).length;
  return openSpans > closeSpans;
}

function hasWordBoundary(text, index, length) {
  const before = index > 0 ? text[index - 1] : "";
  const after = text[index + length] || "";
  if (before && /[A-Za-z0-9]/.test(before)) return false;
  if (after && /[A-Za-z0-9]/.test(after)) return false;
  return true;
}

function nextPlainIndex(text, token, from) {
  let cursor = from;
  while (cursor < text.length) {
    const index = text.indexOf(token, cursor);
    if (index < 0) return -1;
    if (!isInsideSpan(text, index) && hasWordBoundary(text, index, token.length)) {
      return index;
    }
    cursor = index + token.length;
  }
  return -1;
}

export function wrapUnknownMedications(text, findings = []) {
  if (!text || !findings.length) return text;
  const unique = [];
  const seen = new Set();
  const ordered = [...findings].sort(
    (a, b) => (b.originalText?.length || 0) - (a.originalText?.length || 0)
  );
  for (const finding of ordered) {
    const token = finding?.originalText;
    if (!token || seen.has(token)) continue;
    seen.add(token);
    unique.push(finding);
  }

  let result = text;
  for (const finding of unique) {
    const token = finding.originalText;
    let searchFrom = 0;
    let local = 0;
    let guard = 0;
    while (guard < 20) {
      guard += 1;
      const index = nextPlainIndex(result, token, searchFrom);
      if (index < 0) break;
      const span = `<span class="unknown-medicine" data-unknown-med-id="${escapeHtml(finding.id)}" data-unknown-med-text="${escapeHtml(encodeURIComponent(token))}" data-unknown-med-index="${local}" style="background:#fef3c7;color:#92400e;font-weight:600;text-decoration:underline;text-underline-offset:2px;cursor:pointer;border-radius:3px;padding:0 2px;" title="Unknown medicine. Click for a suggested correction.">${escapeHtml(token)}</span>`;
      result = result.slice(0, index) + span + result.slice(index + token.length);
      searchFrom = index + span.length;
      local += 1;
    }
  }
  return result;
}

function isMedicineToken(text, index, length) {
  const before = index > 0 ? text[index - 1] : "";
  const after = text[index + length] || "";
  if (before && /[A-Za-z0-9]/.test(before)) return false;
  if (after && /[A-Za-z0-9-]/.test(after)) return false;
  return true;
}

export function replaceMedicineNames(text, pairs) {
  const source = text || "";
  const usable = (pairs || [])
    .filter((pair) => pair?.originalText && pair?.replacement && pair.originalText !== pair.replacement)
    .sort((a, b) => b.originalText.length - a.originalText.length);
  if (!usable.length) return { text: source, count: 0 };

  let count = 0;
  let result = "";
  let index = 0;
  while (index < source.length) {
    const beforeOk = index === 0 || !/[A-Za-z0-9]/.test(source[index - 1]);
    let matched = null;
    if (beforeOk) {
      for (const pair of usable) {
        if (!source.startsWith(pair.originalText, index)) continue;
        if (!isMedicineToken(source, index, pair.originalText.length)) continue;
        matched = pair;
        break;
      }
    }
    if (!matched) {
      result += source[index];
      index += 1;
      continue;
    }
    result += matched.replacement;
    index += matched.originalText.length;
    count += 1;
  }
  return { text: result, count };
}

export function replaceOneMedicineName(sectionText, originalText, replacement, blockText, localOccurrence = 0) {
  const source = sectionText || "";
  if (!originalText || !replacement || originalText === replacement) {
    return { text: source, count: 0 };
  }
  const occurrence = Number.isInteger(localOccurrence) && localOccurrence >= 0 ? localOccurrence : 0;
  if (blockText && blockText.includes(originalText)) {
    let from = 0;
    let seen = 0;
    let local = -1;
    while (from <= blockText.length) {
      const at = blockText.indexOf(originalText, from);
      if (at < 0) break;
      if (isMedicineToken(blockText, at, originalText.length)) {
        if (seen === occurrence) {
          local = at;
          break;
        }
        seen += 1;
      }
      from = at + originalText.length;
    }
    if (local >= 0) {
      const contextStart = Math.max(0, local - 40);
      const anchor = blockText.slice(contextStart, local + originalText.length);
      const anchorAt = source.indexOf(anchor);
      if (anchorAt >= 0) {
        const start = anchorAt + (local - contextStart);
        return {
          text: source.slice(0, start) + replacement + source.slice(start + originalText.length),
          count: 1,
        };
      }
    }
  }
  let from = 0;
  let seen = 0;
  while (from <= source.length) {
    const at = source.indexOf(originalText, from);
    if (at < 0) break;
    if (isMedicineToken(source, at, originalText.length)) {
      if (seen === occurrence) {
        return {
          text: source.slice(0, at) + replacement + source.slice(at + originalText.length),
          count: 1,
        };
      }
      seen += 1;
    }
    from = at + originalText.length;
  }
  return { text: source, count: 0 };
}
